import Caller from './caller.js'
import TextsHelper from '@/custom/classes/texts_helper.js'
import Settings from '@/custom/classes/settings.js'
import { openInNewTab, getOwner, emit } from '@/custom/classes/my_utils.js'
import { MAX_SENIORITY_CHECKS_PATH } from '@/custom/classes/max_seniority_check.js'
import { JOB_TITLE_RANKS_PATH, jobTitleProfilePath } from '@/custom/classes/applicant_job_titles.js'

class NetworkUtils {
  constructor () {
    this.caller = new Caller()
    this.texts_helper = new TextsHelper()
    this.settings = new Settings()
  }

  setRouter (route) {
    this.route = route
  }

  getShowId () {
    return this.route.query.show_id == undefined ? '' : this.route.query.show_id
  }

  getAllApplicants (params) {
    let validated_params = params == undefined ? {} : params
    validated_params.resource_type = 'Applicant'
    return this.caller.request(
      'get',
      '/users/' + this.getShowId(),
      'admin_headers',
      validated_params
    )
  }

  getApplicants (params) {
    if (this.getShowId()) {
      return this.caller.request('get', '/applicants/' + this.getShowId(), 'admin_headers', params)
    }
    return this.caller.request('get', '/applicants', 'admin_headers', params)
  }

  getApplicantsByGreenOffers (params) {
    let validated_params = params == undefined ? {} : params
    validated_params.resource_type = 'Applicant'
    return this.caller.request('get', '/index_grouped_by_green_offers/', 'admin_headers', params)
  }

  getAdminUser (params) {
    return this.caller.request('get', '/users/1', 'admin_headers', params)
  }

  getCompanyOneTimeCode (params) {
    return this.caller.request('get', '/generate_company_one_time_code/', 'admin_headers', params)
  }

  getOwnersSchedule (params) {
    return this.caller.request('get', '/owners_schedule/', 'admin_headers', params)
  }

  getOwnerSchedule (params) {
    return this.caller.request('get', '/owners_schedule/' + params.id, 'admin_headers', params)
  }

  getVettingRecipe (params) {
    return this.caller.request('get', '/vetting_recipes/' + params.id, 'admin_headers', params)
  }

  getReferralLink (params) {
    return this.caller.request('get', '/referrals/1', 'shared_headers', params)
  }

  getCounters (params = {}) {
    return this.caller.request('get', '/counters/', 'admin_headers', params)
  }

  refreshPublicPositions () {
    return this.postEvent({ event: 'refresh_public_positions', current_type: 'Applicant', current_resource_id: 1 })
  }

  login (params) {
    return this.caller.request('post', '/login_plain/', 'applicant_headers', params)
  }

  duplicatePosition (params) {
    return this.caller.request('post', '/duplicate_position/', 'admin_headers', params)
  }

  getAllFeedDesignators (params) {
    return this.caller.request('get', '/feed_designators/', 'admin_headers', params)
  }

  getFeedGroupList (params) {
    return this.caller.request('get', '/get_feed_group_list', 'admin_headers', params)
  }

  getFeedDesignator (params) {
    return this.caller.request('get', '/feed_designators/' + params.id, 'admin_headers', params)
  }

  getAllCompanies (params) {
    let validated_params = params == undefined ? {} : params
    validated_params.resource_type = 'Company'
    return this.caller.request(
      'get',
      '/users/' + this.getShowId(),
      'admin_headers',
      validated_params
    )
  }

  getCompanyEmployees (params) {
    return this.caller.request('get', '/company_employees/', 'admin_headers', params)
      .then(response => {
        // The API returns { data: [...], meta: {...} } format
        // Extract the data array for the component
        return response.data && Array.isArray(response.data) ? response.data : (response.data || [])
      })
  }

  getRelatedOffers (params) {
    return this.caller.request('get', '/get_related_offers/', 'admin_headers', params)
  }

  getOfferBy (params) {
    return this.caller.request('get', '/offer_by/', 'admin_headers', params)
  }

  getFiles (params) {
    return this.caller.request('get', '/files', 'admin_headers', params)
  }

  // Content-based pick of the candidate's real (non-confidential) CV.
  // Resolves to { name, uploaded_at }; rejects when no such CV exists (404).
  getOriginalCv (params) {
    return this.caller.request('get', '/files/original_cv', 'admin_headers', params)
  }

  getFile (params) {
    return this.caller.request('get', '/files/1/', 'admin_headers', { ...params, responseType: 'blob' })
  }

  getOutsourcePosition (params) {
    return this.caller.request('get', '/shuffle_positions/1', 'admin_headers', params)
  }

  getOutsourcePositions (params) {
    return this.caller.request('get', '/shuffle_positions/', 'admin_headers', params)
  }

  generateOffers (params, custom_params) {
    return this.postEvent({
      current_type: params.current_type,
      current_resource_id: params.current_resource_id,
      event: 'resource_updated',
      ...custom_params
    })
  }

  aiWorkPosition (params) {
    return this.caller.request('post', '/ai_work_position', 'admin_headers', params)
  }

  getUser (user_id) {
    return this.caller.request('get', '/users/' + user_id, 'admin_headers')
  }

  getUsers (params = {}) {
    return this.caller.request('get', '/users', 'admin_headers', params)
  }

  getBlog (params) {
    return this.caller.request('get', '/blogs/' + params.id, 'admin_headers', params)
  }

  previewApplicantProfilePDF (params) {
    return this.caller.request('post', '/preview_applicant_profile_pdf/', 'admin_headers', { ...params, responseType: 'blob' })
  }

  getApplicantRawText (params) {
    return this.caller.request('get', '/get_raw_text/', 'admin_headers', { ...params })
  }

  getApplicant (params) {
    return this.caller.request('get', '/applicants/' + params.id, 'admin_headers')
  }

  getCompany (params) {
    return this.caller.request('get', '/companies/' + params.id, 'admin_headers')
  }

  updateCompany (params = {}) {
    return this.caller.request('put', '/companies/' + params.id, 'admin_headers', params)
  }

  getAlert (alert_id) {
    return this.caller.request('get', '/alerts/' + alert_id, 'admin_headers')
  }

  getAlertForFeed (params) {
    return this.caller.request('get', '/get_alert_for_feed', 'admin_headers', params)
  }

  getAlertForFeedWithMonday (params) {
    return this.caller.request('get', '/get_alert_for_feed_with_monday', 'admin_headers', params)
  }

  getAlertForFeedWithMondayManual (params) {
    return this.caller.request('post', '/get_alert_for_feed_with_monday_manual', 'admin_headers', params)
  }

  peekNextOfferIds (params) {
    return this.caller.request('post', '/peek_next_offer_ids', 'admin_headers', params)
  }

  getPosition (params) {
    return this.caller.request('get', '/positions/' + params.id, 'admin_headers')
  }

  getEducationDegree (params) {
    return this.caller.request('get', '/education_degrees/' + params.id, 'admin_headers', params)
  }

  deleteEducationDegree (params) {
    return this.caller.request('delete', '/education_degrees/' + params.id, 'admin_headers', params)
  }

  deleteRelatedResource (params) {
    return this.caller.request('delete', '/related_resources/' + params.id, 'admin_headers', params)
  }

  deleteBlog (params) {
    return this.caller.request('delete', '/blogs/' + params.id, 'admin_headers', params)
  }

  deleteFeedDesignator (params) {
    return this.caller.request('delete', '/feed_designators/' + params.id, 'admin_headers', params)
  }

  getFeedReport (params) {
    return this.caller.request('get', '/feed_reports/', 'admin_headers', params)
  }

  deleteHujiPosition (params) {
    return this.caller.request('delete', '/huji_positions/' + params.id, 'admin_headers', params)
  }

  deleteVettingRecipe (params) {
    return this.caller.request('delete', '/vetting_recipes/' + params.id, 'admin_headers', params)
  }

  getApplicantHistoryCompany (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request('get', '/applicants/history_companies/' + params.applicant_history_company_id, 'applicant_headers', validated_params)
  }

  getHistoryCompany (history_company_id) {
    return this.caller.request('get', '/history_companies/' + history_company_id, 'admin_headers')
  }

  getSkill (params) {
    return this.caller.request('get', '/skills/' + params.id, 'admin_headers')
  }

  getApplicantLanguage (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request('get', '/applicants/languages/' + params.applicant_language_id, 'applicant_headers', validated_params)
  }

  getApplicantLanguages (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request(
      'get',
      '/applicants/languages/',
      'applicant_headers',
      validated_params
    )
  }

  searchHistoryCompany (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller
      .request(
        'get',
        '/history_companies',
        'shared_headers',
        validated_params
      )
  }

  getApplicantEducation (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request('get', '/applicants/educations/' + params.applicant_education_id, 'applicant_headers', validated_params)
  }

  getApplicantSuggestedPositions (params) {
    return this.caller.request('get', '/get_applicant_suggested_positions', 'admin_headers', params)
  }

  getCompanyEducation (params) {
    return this.caller.request('get', '/companies/educations/' + params.education_id, 'company_headers', params)
  }

  getPositionEducation (params) {
    return this.caller.request('get', '/positions/' + params.position_id + '/educations/' + params.education_id, 'company_headers', params)
  }

  getPositionLanguage (params) {
    return this.caller.request('get', '/positions/' + params.position_id + '/languages/' + params.position_language_id, 'company_headers', params)
  }

  getEducationInstitution (params) {
    return this.caller.request('get', '/education_institutions/' + params.id, 'admin_headers', params)
  }

  postEducationInstitution (params) {
    return this.caller.request('post', '/education_institutions', 'admin_headers', params)
  }

  postEducationCourse (params) {
    return this.caller.request('post', '/education_courses', 'admin_headers', params)
  }

  postEducationDegree (params) {
    return this.caller.request('get', '/education_degrees', 'admin_headers', params)
  }

  getAllLanguages () {
    return this.caller.request('get', '/languages/', 'admin_headers')
  }

  getAllHistoryCompanies () {
    return this.caller.request('get', '/history_companies/', 'admin_headers')
  }

  getAllSkills (params) {
    return this.caller.request('get', '/skills/', 'admin_headers', params)
  }

  getAllEducationDegrees (params = {}) {
    return this.caller.request('get', '/education_degrees/', 'admin_headers', params)
  }

  getAllEducationCourses (params = {}) {
    return this.caller.request('get', '/education_courses/', 'admin_headers', params)
  }

  deleteEducationCourse (params) {
    return this.caller.request('delete', '/education_courses/' + params.id, 'admin_headers', params)
  }

  getAllEducationInstitutions (params = {}) {
    return this.caller.request('get', '/education_institutions/', 'admin_headers', params)
  }

  getAllFilters () {
    return this.caller.request('get', '/filters/', 'admin_headers')
  }

  getAllScreens () {
    return this.caller.request('get', '/screens/', 'admin_headers')
  }

  getAllQuestions () {
    return this.caller.request('get', '/questions/', 'admin_headers')
  }

  getAllTasks () {
    return this.caller.request('get', '/tasks/', 'admin_headers')
  }

  getAllHujiPositions (params) {
    return this.caller.request('get', '/huji_positions/', 'admin_headers', params)
  }

  getHujiPosition (params) {
    return this.caller.request('get', '/huji_positions/' + params.id, 'admin_headers', params)
  }

  getAllBlogs () {
    return this.caller.request('get', '/blogs/', 'admin_headers')
  }

  getApplicantHistoryCompanies (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request(
      'get',
      '/applicants/history_companies/',
      'applicant_headers',
      validated_params
    )
  }

  getApplicantEducations (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request(
      'get',
      '/applicants/educations/',
      'applicant_headers',
      validated_params
    )
  }

  getApplicantTexts (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request(
      'get',
      '/applicants/educations/',
      'applicant_headers',
      validated_params
    )
  }

  getPositionEducations (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request(
      'get',
      '/positions/' +
        params.position_id +
        '/educations/',
      'company_headers',
      validated_params
    )
  }

  getPositionLanguages (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request(
      'get',
      '/positions/' +
        params.position_id +
        '/languages/',
      'company_headers',
      validated_params
    )
  }

  getPositionSkills (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request(
      'get',
      '/positions/' +
        params.position_id +
        '/skills/',
      'company_headers',
      validated_params
    )
  }

  getPositionAiSkills (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request(
      'get',
      '/positions/' +
        params.position_id +
        '/ai_skills/',
      'company_headers',
      validated_params
    )
  }

  getCompanyHistoryCompanies (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request(
      'get',
      '/history_companies/',
      'company_headers',
      validated_params
    )
  }

  getCompanyEducations (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request(
      'get',
      '/companies/educations/',
      'company_headers',
      validated_params
    )
  }

  getResourceMeeting (params) {
    return this.caller.request('get', '/meetings/' + params.meeting_id, 'shared_headers', params)
  }

  getMeeting (params) {
    return this.caller.request('get', '/meetings/' + params.meeting_id, 'admin_headers', params)
  }

  getResourceOffer (params) {
    return this.caller.request(
      'get',
      '/offers/' + params.offer_id,
      'admin_headers',
      params
    )
  }

  getResourceAlerts (params) {
    return this.caller.request('get', '/alerts/', 'admin_headers', params)
  }

  getResourceOffers (params) {
    let validated_params = params == undefined ? {} : params
    // Extract controller from params to prevent it from being serialized in URL
    const { controller, ...cleanParams } = validated_params
    return this.caller.request(
      'get',
      '/offers/' + (params.show_id || ''),
      'admin_headers',
      cleanParams,
      controller
    )
  }

  getStargateLog (params) {
    let validated_params = params == undefined ? {} : params
    const { controller, ...cleanParams } = validated_params
    return this.caller.request('get', '/stargate_log', 'admin_headers', cleanParams, controller)
  }

  // "משפך 24 שעות" — one call returns every pipeline stage plus the two
  // sub-metrics. Read-only; safe to poll.
  getPipelineFunnel (params) {
    let validated_params = params == undefined ? {} : params
    const { controller, ...cleanParams } = validated_params
    return this.caller.request('get', '/pipeline_funnel', 'admin_headers', cleanParams, controller)
  }

  // Drilldown behind the funnel's "הצעות שנשלחו למועמדים" stage: one row per
  // candidate with the offers pitched to them and the response per offer.
  // Read-only; fetched on demand when the stage is expanded.
  getPipelineFunnelPitched (params) {
    let validated_params = params == undefined ? {} : params
    const { controller, ...cleanParams } = validated_params
    return this.caller.request('get', '/pipeline_funnel/pitched', 'admin_headers', cleanParams, controller)
  }

  // Drilldown behind the funnel's "פילוח BA" stage: the offers behind each
  // green/orange/red bar, grouped by verdict, each with the applicant, position
  // and company it is about. Read-only; fetched on demand when the stage is
  // expanded.
  getPipelineFunnelBaBreakdown (params) {
    let validated_params = params == undefined ? {} : params
    const { controller, ...cleanParams } = validated_params
    return this.caller.request('get', '/pipeline_funnel/ba_breakdown', 'admin_headers', cleanParams, controller)
  }

  // The list behind the funnel's 8->9 drop-off strip: the offers that were
  // supposed to reach BA in the window and did not, grouped by the reason the
  // server could prove. Read-only; fetched when the strip is opened.
  getPipelineFunnelDropOff (params) {
    let validated_params = params == undefined ? {} : params
    const { controller, ...cleanParams } = validated_params
    return this.caller.request('get', '/pipeline_funnel/drop_off', 'admin_headers', cleanParams, controller)
  }

  getChatbotProposals (params) {
    let validated_params = params == undefined ? {} : params
    const { controller, ...cleanParams } = validated_params
    return this.caller.request('get', '/chatbot_proposals', 'admin_headers', cleanParams, controller)
  }

  getChatbotProposal (params) {
    return this.caller.request('get', '/chatbot_proposals/' + params.id, 'admin_headers')
  }

  handleChatbotProposal (params) {
    return this.caller.request('post', '/chatbot_proposals/' + params.id + '/handle', 'admin_headers', {
      recruiter_note: params.recruiter_note,
      complex_rejection: params.complex_rejection,
      category: params.category
    })
  }

  recalculateChatbotProposal (params) {
    return this.caller.request('post', '/chatbot_proposals/' + params.id + '/recalculate', 'admin_headers')
  }

  recalculateChatbotProposalCategory (params) {
    return this.caller.request('post', '/chatbot_proposals/' + params.id + '/recalculate_category', 'admin_headers', {
      label: params.label
    })
  }

  updateChatbotProposalStatus (params) {
    return this.caller.request('put', '/chatbot_proposals/' + params.id, 'admin_headers', {
      status: params.status
    })
  }

  markChatbotProposalComplex (params) {
    return this.caller.request('post', '/chatbot_proposals/' + params.id + '/mark_complex', 'admin_headers', {
      note: params.note
    })
  }

  // Disagreement UI — Case A (single explanation) + Case B (items[]). rebase_server
  // relays server-to-server to Better Agents; the browser never calls it directly.
  // `request_id` is one uuid per modal SESSION (idempotent replay); each item may
  // carry `mode` ('add_new' | 'edit') and `feedback_id` (which existing rule to edit).
  submitDisagreementFeedback (params) {
    return this.caller.request('post', '/chatbot_proposals/' + params.id + '/disagreement_feedback', 'admin_headers', {
      direction: params.direction,
      explanation: params.explanation,
      items: params.items,
      request_id: params.request_id
    })
  }

  // Existing coexisting rules for one (proposal, direction, dimension) — drives the
  // per-dimension "existing rules" card (per-row Edit vs a single Add-new). Server
  // is 404/error-tolerant, so a failure degrades to plain Add-new rather than blocking.
  getDisclosure (params) {
    return this.caller.request('get', '/chatbot_proposals/' + params.id + '/disclosure', 'admin_headers', {
      direction: params.direction,
      implicated_agent_type: params.implicated_agent_type
    })
  }

  // Disagreement UI — Case C. rebase_server relays to the jira task API.
  reportNeedsInfo (params) {
    return this.caller.request('post', '/chatbot_proposals/' + params.id + '/needs_info_report', 'admin_headers', {
      recruiter_text: params.recruiter_text
    })
  }

  // Structured rejection (Phase 1, reason now sampled). Recruiter rejects a
  // chatbot proposal. The reason fields are OPTIONAL — the caller asks for them
  // only on the 1-in-3 draw (shouldAskRejectionReason). When a reason IS sent,
  // rebase_server validates it (422 on bad category/length) and relays it to
  // Better Agents on a disagreement. POSTs the existing /handle endpoint with
  // action=reject.
  rejectChatbotProposal (params) {
    return this.caller.request('post', '/chatbot_proposals/' + params.id + '/handle', 'admin_headers', {
      action: 'reject',
      rejection_reason_category: params.rejection_reason_category,
      rejection_reason_text: params.rejection_reason_text
    })
  }

  // Defer ("דחה לשלב מאוחר") — the feed-cleanup snooze, split from a real reject.
  // Sets recruiter_action='defer' and moves the offer to awaiting_admin_later. No
  // reason required; never relayed to Better Agents as a rejection.
  deferChatbotProposal (params) {
    return this.caller.request('post', '/chatbot_proposals/' + params.id + '/handle', 'admin_headers', {
      action: 'defer'
    })
  }

  getResourceText (params) {
    return this.caller.request('get', '/resource_texts/' + params.designator, 'shared_headers', params)
  }

  getResourceTexts (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request(
      'get',
      '/resource_texts/',
      'shared_headers',
      validated_params
    )
  }

  getResourceMeetings (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request(
      'get',
      '/meetings/',
      'shared_headers',
      validated_params
    )
  }

  getResourceLocations (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request(
      'get',
      '/locations/',
      'shared_headers',
      validated_params
    )
  }

  getCompanyPositions (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request(
      'get',
      '/positions/' + (validated_params.position_id || ''),
      'admin_headers',
      validated_params
    )
  }

  getAllPositions (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request(
      'get',
      '/positions/' + this.getShowId(),
      'admin_headers',
      validated_params
    )
  }

  getPositions (params = {}) {
    return this.caller.request('get', '/positions/', 'admin_headers', params)
  }

  getAllAlerts (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request(
      'get',
      '/alerts/',
      'admin_headers',
      validated_params
    )
  }

  getAllMeetings (params) {
    return this.caller.request(
      'get',
      '/meetings/' + '?sort=' + params.sort,
      'admin_headers'
    )
  }

  getStat (stat_id) {
    return this.caller.request('get', '/stats/' + stat_id, 'admin_headers')
  }

  getStats (params = {}) {
    return this.caller.request('get', '/stats/', 'admin_headers', params)
  }

  updateStat (params = {}) {
    return this.caller.request('put', '/stats/' + params.id, 'admin_headers', { ...params, created_by: 'Admin' })
  }

  postStat (params = {}) {
    return this.caller.request('post', '/stats/', 'admin_headers', { ...params, created_by: 'Admin' })
  }

  getLanguage (params) {
    return this.caller.request('get', '/languages/' + params.id, 'admin_headers', params)
  }

  getInheritanceText (params) {
    return new Promise((resolve, reject) => {
      this.getResourceTexts({ current_type: 'Position', current_resource_id: params.position_id }).then(response => {
        let text = this.texts_helper.getTextByDesignator(response, params.designator)
        if (text != undefined && text.content.length > 0) {
          resolve(text)
          return
        }
        this.getResourceTexts({ current_type: 'Company', current_resource_id: params.company_id }).then(() => {
          resolve(text)
        })
      })
    })
  }

  getInheritanceQuestion (params) {
    return new Promise((resolve, reject) => {
      this.getResourceQuestions({ current_type: 'Position', current_resource_id: params.position_id, designators: params.designator }).then(response => {
        if (response[0].resource_answers.length > 0) {
          resolve(response[0])
          return
        }
        this.getResourceQuestions({ current_type: 'Company', current_resource_id: params.company_id, designators: params.designator }).then(response => {
          resolve(response[0])
        })
      })
    })
  }

  getInheritanceLocation (params) {
    return new Promise((resolve, reject) => {
      this.getResourceLocations({ current_type: 'Position', current_resource_id: params.position_id }).then(response => {
        if (response[0] != undefined) {
          resolve(response)
          return
        }
        this.getResourceLocations({ current_type: 'Company', current_resource_id: params.company_id }).then(response => {
          resolve(response)
        })
      })
    })
  }

  getResourceQuestions (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request(
      'get',
      '/questions/',
      'shared_headers',
      validated_params
    )
  }

  getApplicantSkills (params) {
    let validated_params = params == undefined ? {} : params
    return this.caller.request(
      'get',
      '/applicants/skills/',
      'applicant_headers',
      validated_params
    )
  }

  downloadCv (params) {
    return this.getResourceTexts(params).then(response => {
      let outsource_cv_path = this.texts_helper.getTextByDesignator(response, 'outsource_cv_path')
      let link = outsource_cv_path.content
      openInNewTab(link)
    })
  }

  getRelatedResources (params) {
    return this.caller.request('get', '/related_resources/', 'admin_headers', params)
  }

  getRelatedResource (params) {
    return this.caller.request('get', '/related_resources/' + params.id, 'admin_headers', params)
  }

  getEducationCourse (params) {
    return this.caller.request('get', '/education_courses/' + params.id, 'admin_headers', params)
  }

  getQuestions (params) {
    return this.caller.request('get', '/questions/', 'admin_headers', params)
  }

  getAdminQuestion (params) {
    return this.caller.request('get', '/questions/' + params.id, 'admin_headers', params)
  }

  getQuestionInfos (params) {
    return this.caller.request('get', '/questions/' + params.question_id + '/infos/', 'admin_headers', params)
  }

  getQuestionOptions (params) {
    return this.caller.request('get', '/questions/' + params.question_id + '/options/', 'admin_headers', params)
  }

  getQuestionOption (params) {
    return this.caller.request('get', '/questions/' + params.question_id + '/options/' + params.id, 'admin_headers', params)
  }

  getQuestionInfo (params) {
    return this.caller.request('get', '/questions/' + params.question_id + '/infos/' + params.id, 'admin_headers', params)
  }

  // Runs the offer process on the position itself. The backend ignores a
  // Company resource for offers, so posting Company here was a silent no-op.
  sendPositionUpdateEvent (params) {
    // Some callers have no position id (e.g. a misspelled payload key); keep
    // that a harmless no-op instead of a 422 from the backend.
    if (params.position_id === undefined || params.position_id === null || params.position_id === '') {
      return Promise.resolve(null)
    }
    return this.postEvent({
      current_type: 'Position',
      current_resource_id: params.position_id,
      position_id: params.position_id,
      event: 'resource_updated'
    })
  }

  createOutsourcePosition (params) {
    return this.caller.request('post', '/shuffle_positions/', 'admin_headers', params)
  }

  positionCreationAssistant (params) {
    return this.caller.request('post', '/position_creation_assistant', 'admin_headers', params)
  }

  ratePositionAssistant (params) {
    return this.caller.request('post', '/position_creation_assistant/rating', 'admin_headers', params)
  }

  createPosition (params) {
    return this.caller.request('post', '/positions/', 'admin_headers', params)
  }

  createRelatedResource (params) {
    return this.caller.request('post', '/related_resources/', 'admin_headers', params)
  }

  postResourceText (params) {
    return this.caller.request('post', '/resource_texts/', 'shared_headers', params)
  }

  postLocation (params) {
    return this.caller.request('post', '/locations/', 'shared_headers', params)
  }

  postQuestionAnswer (params) {
    return this.caller.request('post', '/questions/' + params.question_id + '/answers/', 'shared_headers', params)
  }

  postUser (params) {
    return this.caller.request('post', '/users/', 'admin_headers', params)
  }

  postHistoryCompany (params) {
    return this.caller.request('post', '/history_companies/', 'admin_headers', params)
  }

  postText (params) {
    return this.caller.request('post', '/resource_texts/', 'shared_headers', params)
  }

  updateOutsourcePosition (params) {
    return this.caller.request('put', '/shuffle_positions/1', 'admin_headers', params)
  }

  boostOutsourcePosition (params) {
    return this.caller.request('post', '/boost_outsource_position/', 'admin_headers', params)
  }

  updateUser (params) {
    return this.caller.request('put', '/users/' + params.user_id, 'admin_headers', params)
  }

  updateCompanyEmployee (params) {
    if (params.id) {
      return this.caller.request('put', '/company_employees/' + params.id, 'admin_headers', params)
    } else {
      return this.caller.request('post', '/company_employees/', 'admin_headers', params)
    }
  }

  updateOffer (params) {
    return this.caller.request('put', '/offers/' + params.id, 'admin_headers', params)
  }

  updateAlert (params) {
    return this.caller.request('put', '/alerts/' + params.id, 'admin_headers', params)
  }

  massUpdateAlert (params) {
    return this.caller.request('patch', '/alerts/', 'admin_headers', params)
  }

  updatePosition (params) {
    return this.caller.request('put', '/positions/' + params.position_id, 'admin_headers', params)
  }

  // body: { checks: [ { position_id, expected_max } ] } — see max_seniority_check.js
  confirmMaxSeniorityChecks (params) {
    return this.caller.request('put', MAX_SENIORITY_CHECKS_PATH, 'admin_headers', params)
  }

  updatePositionSkill (params) {
    return this.caller.request('put', '/positions/' + params.position_id + '/skills/' + params.id, 'company_headers', params)
  }

  updateApplicant (params) {
    return this.caller.request('put', '/applicants/' + params.applicant_id, 'admin_headers', params)
  }

  updateApplicantSkill (params) {
    return this.caller.request('put', '/applicants/skills/' + params.id, 'applicant_headers', params)
  }

  updateRelatedResource (params) {
    return this.caller.request('put', '/related_resources/' + params.id, 'admin_headers', params)
  }

  postApplicantSkill (params) {
    return this.caller.request('post', '/applicants/skills/', 'applicant_headers', params)
  }

  linkCompanyHistoryCompany (params) {
    return this.caller.request('post', '/history_companies/', 'company_headers', params)
  }

  getCompaniesByHistoryCompany (params) {
    return this.caller.request('get', `/companies?history_company_id=${params.history_company_id}`, 'admin_headers')
  }

  postCompanyEducation (params) {
    return this.caller.request('post', '/companies/educations/', 'company_headers', params)
  }

  postBlog (params) {
    return this.caller.request('post', '/blogs/', 'admin_headers', params)
  }

  updateBlog (blog_id, params) {
    return this.caller.request('put', '/blogs/' + blog_id, 'admin_headers', params)
  }

  uploadFile (form_data) {
    return this.caller.request('post', '/files/', 'admin_headers', form_data)
  }

  deleteFile (params) {
    return this.caller.request('delete', '/files/1', 'admin_headers', params)
  }

  updateFile (params) {
    return this.caller.request('put', '/files/1', 'admin_headers', params)
  }

  postEvent (params) {
    return this.caller.request('post', 'events/', 'admin_headers', params)
  }

  postGeneralNotify (params) {
    return this.caller.request('post', '/general_notify/', 'applicant_headers', params)
  }

  postAlert (params) {
    return this.caller.request('post', '/alerts/', 'admin_headers', params)
  }

  postDoneAlert (params, status) {
    this.updateAlert({ id: params.id, status: status }).then(() => {
      emit('show_toast', { text: 'Done' })
      this.postStat({
        resource_type: params.resource_type,
        resource_id: params.resource_id,
        owner: getOwner().name,
        title: params.title,
        content: params.content + `, new status is: ${status}`,
        designator: 'manual',
        status: status,
        priority: 5
      })
    })
  }

  postPositionSkill (params) {
    return this.caller.request('post', '/positions/' + params.position_id + '/skills/', 'company_headers', params)
  }

  deleteEducationInstitution (params) {
    return this.caller.request('delete', '/education_institutions/' + params.id, 'admin_headers', params)
  }

  deleteSkill (params) {
    return this.caller.request('delete', '/skills/' + params.id, 'admin_headers', params)
  }

  deleteHistoryCompany (params) {
    return this.caller.request('delete', '/history_companies/' + params.id, 'admin_headers', params)
  }

  deleteApplicantEducation (params) {
    return this.caller.request('delete', '/applicants/educations/' + params.id, 'applicant_headers', params)
  }

  deleteApplicantHistoryCompany (params) {
    return this.caller.request('delete', '/applicants/history_companies/' + params.id, 'applicant_headers', params)
  }

  deleteApplicantLanguage (params) {
    return this.caller.request('delete', '/applicants/languages/' + params.id, 'applicant_headers', params)
  }

  deletePositionSkill (params) {
    return this.caller.request('delete', '/positions/' + params.position_id + '/skills/' + params.id, 'company_headers', params)
  }

  deleteApplicantSkill (params) {
    return this.caller.request('delete', '/applicants/skills/' + params.id, 'applicant_headers', params)
  }

  deleteLocation (params) {
    return this.caller.request('delete', '/locations/' + params.id, 'shared_headers', params)
  }

  deleteMeeting (params) {
    return this.caller.request('delete', '/meetings/' + params.id, 'shared_headers', params)
  }

  deleteLanguage (params) {
    return this.caller.request('delete', '/languages/' + params.id, 'admin_headers', params)
  }

  deleteQuestionOption (params) {
    return this.caller.request('delete', '/questions/' + params.question_id + '/options/' + params.id, 'admin_headers', params)
  }

  deleteCompanyEducation (params) {
    return this.caller.request('delete', '/companies/educations/' + params.id, 'company_headers', params)
  }

  deletePositionEducation (params) {
    return this.caller.request('delete', '/positions/' + params.position_id + '/educations/' + params.id, 'company_headers', params)
  }

  deletePositionLanguage (params) {
    return this.caller.request('delete', '/positions/' + params.position_id + '/languages/' + params.id, 'company_headers', params)
  }

  searchCompany (params) {
    return this.caller.request('get', '/companies/', 'admin_headers', params)
  }

  getAccountingCompany (params) {
    return this.caller.request('get', '/get_accounting_company/', 'admin_headers', params)
  }

  getAccountingOffer (params) {
    return this.caller.request('get', '/get_accounting_offer/', 'admin_headers', params)
  }

  searchAccountingCompany (params) {
    return this.caller.request('get', '/search_accounting_company/', 'admin_headers', params)
  }

  createAccountingCompany (params) {
    return this.caller.request('post', '/create_accounting_company/', 'admin_headers', params)
  }

  updateAccountingOffer (params) {
    return this.caller.request('put', '/update_accounting_offer/', 'admin_headers', params)
  }

  getVettingRecipes (params) {
    return this.caller.request('get', '/vetting_recipes/', 'admin_headers', params)
  }

  getServiceRecommendations (params) {
    return this.caller.request('get', `/service_recommendation/`, 'admin_headers', params)
  }

  getServiceRecommendation (params) {
    return this.caller.request('get', `/service_recommendation/${params.id}`, 'admin_headers', params)
  }

  postServiceRecommendation (params) {
    return this.caller.request('post', '/service_recommendation/', 'admin_headers', params)
  }

  deleteServiceRecommendation (params) {
    return this.caller.request('delete', `/service_recommendation/${params.id}`, 'admin_headers', params)
  }

  updateServiceRecommendation (params) {
    return this.caller.request('put', `/service_recommendation/${params.id}`, 'admin_headers', params)
  }

  getAdmins (params) {
    return this.caller.request('get', `/admins/`, 'admin_headers', params)
  }

  getAdmin (params) {
    return this.caller.request('get', `/admins/${params.id}`, 'admin_headers', params)
  }

  postAdmin (params) {
    return this.caller.request('post', '/admins/', 'admin_headers', params)
  }

  deleteAdmin (params) {
    return this.caller.request('delete', `/admins/${params.id}`, 'admin_headers', params)
  }

  updateAdmin (params) {
    return this.caller.request('put', `/admins/${params.id}`, 'admin_headers', params)
  }

  resetPassword (params) {
    return this.caller.request('put', `/update_password/`, 'admin_headers', params)
  }

  exposeOffer (params) {
    return this.caller.request('post', '/expose_offer', 'admin_headers', params)
  }

  getTag (params) {
    return this.caller.request('get', `/tags/${params.id}`, 'admin_headers', params)
  }

  getTags (params) {
    return this.caller.request('get', `/tags/`, 'admin_headers', params)
  }

  postTag (params) {
    return this.caller.request('post', '/tags/', 'admin_headers', params)
  }

  deleteTag (params) {
    return this.caller.request('delete', `/tags/${params.id}`, 'admin_headers', params)
  }

  updateTag (params) {
    return this.caller.request('put', `/tags/${params.id}`, 'admin_headers', params)
  }

  getNextBusinessDate (params) {
    return this.caller.request('get', `/business_time`, 'admin_headers', params)
  }

  registerPlain (params) {
    return this.caller.request('post', `/register_plain`, 'applicant_headers', params)
  }

  updateUserDetails (params) {
    return this.caller.request('put', `/applicants/user/1`, { accept: 'applicant api; version=1', Authorization: params.token }, params)
  }

  getSetting (params) {
    return this.caller.request('get', `/settings/${params.id}`, 'admin_headers', params)
  }

  getSettings (params) {
    return this.caller.request('get', `/settings`, 'admin_headers', params)
  }

  updateSetting (params) {
    return this.caller.request('put', `/settings/${params.id}`, 'admin_headers', params)
  }

  getOfferStatus (params) {
    return this.caller.request('get', `/offer_statuses/${params.id}`, 'admin_headers', params)
  }

  getOfferStatuses (params) {
    return this.caller.request('get', `/offer_statuses/`, 'admin_headers', params)
  }

  updateOfferStatuses (params) {
    return this.caller.request('put', `/offer_statuses/${params.id}`, 'admin_headers', params)
  }

  getAdminRequirment (params) {
    return this.caller.request('get', `/admin_requirments/${params.id}`, 'admin_headers', params)
  }

  getAdminRequirments (params) {
    return this.caller.request('get', `/admin_requirments/`, 'admin_headers', params)
  }

  postAdminRequirment (params) {
    return this.caller.request('post', '/admin_requirments/', 'admin_headers', params)
  }

  deleteAdminRequirment (params) {
    return this.caller.request('delete', `/admin_requirments/${params.id}`, 'admin_headers', params)
  }

  updateAdminRequirment (params) {
    return this.caller.request('put', `/admin_requirments/${params.id}`, 'admin_headers', params)
  }

  postEvaluateOffer (params) {
    return this.caller.request('post', `/evaluate_offer/`, 'admin_headers', params)
  }

  getAllJobTitles (params) {
    return this.caller.request('get', `/job_titles/`, 'admin_headers', params)
  }

  createApplicantGenerateAbstract (params) {
    return this.caller.request('post', `/applicants/generate_abstract/`, 'admin_headers', params)
  }

  getApplicantGenerateAbstract (params) {
    return this.caller.request('get', `/applicants/generate_abstract/${params.applicant_id}`, 'admin_headers', params)
  }

  getWhatsappApplicant (params) {
    return this.caller.request('get', `/applicants/latest-applicants`, 'admin_headers', params)
  }

  getApplicantJobTitle (params) {
    return this.caller.request('get', `/applicants/job_titles`, 'admin_headers', params)
  }

  postApplicantJobTitle (params) {
    return this.caller.request('post', `/applicants/job_titles`, 'admin_headers', params)
  }

  updateApplicantJobTitle (params) {
    return this.caller.request('put', `/applicants/job_titles/${params.applicant_id}`, 'admin_headers', params)
  }

  deleteApplicantJobTitle (params) {
    return this.caller.request('delete', `/applicants/job_titles/${params.applicant_id}`, 'admin_headers', params)
  }

  // body: { applicant_id, ordered_job_title_ids: [..<=5], primary_job_title_id? } — see applicant_job_titles.js
  updateApplicantJobTitleRanks (params) {
    return this.caller.request('put', JOB_TITLE_RANKS_PATH, 'admin_headers', params)
  }

  // 200 { ranks_owner, ranks_version, primary_job_title_id, ordered_job_title_ids } on the new back-end; 404 today
  getApplicantJobTitleProfile (params) {
    return this.caller.request('get', jobTitleProfilePath(params.applicant_id), 'admin_headers', params)
  }

  getJobTitleSkills (params) {
    return this.caller.request('get', `/job_titles/${params.id}/skills`, 'admin_headers', params)
  }

  postJobTitleSkills (params) {
    return this.caller.request('post', `/job_titles/${params.id}/skills`, 'admin_headers', params)
  }

  deleteJobTitleSkills (params) {
    return this.caller.request('delete', `/job_titles/${params.id}/skills/${params.skillId}`, 'admin_headers', params)
  }

  getPositionJobTitle (params) {
    return this.caller.request('get', `/positions/job_titles`, 'admin_headers', params)
  }

  postPositionJobTitle (params) {
    return this.caller.request('post', `/positions/job_titles`, 'admin_headers', params)
  }

  updatePositionJobTitle (params) {
    return this.caller.request('put', `/positions/job_titles/${params.position_id}`, 'admin_headers', params)
  }

  deletePositionJobTitle (params) {
    return this.caller.request('delete', `/positions/job_titles/${params.position_id}`, 'admin_headers', params)
  }

  getConversation (params) {
    return this.caller.request('get', `/conversations`, 'admin_headers', params)
  }

  getChatbotActiveConversations (params) {
    return this.caller.request('get', `/admin/v1/chatbot_test/active_conversations`, 'admin_headers', params)
  }

  getChatbotConversationStats (params) {
    return this.caller.request('get', `/admin/v1/chatbot_test/conversation_stats`, 'admin_headers', params)
  }

  getChatbotStrongOffers (params) {
    return this.caller.request('get', `/admin/v1/chatbot_test/strong_offers`, 'admin_headers', params)
  }

  getConvSvcConversations (params) {
    return this.caller.request('get', `/admin/v1/conv_svc/conversations`, 'admin_headers', params)
  }

  getCompanyFollowupPreview (params) {
    return this.caller.request('get', '/admin/v1/company_followup/preview', 'admin_headers', params)
  }

  sendCompanyFollowup (data) {
    return this.caller.request('post', '/admin/v1/company_followup/send', 'admin_headers', data)
  }

  // "מדיניות שליחה" — sending policies. Without company_id the index returns the
  // per-company overview; with it, that company's rules (company-wide + per position).
  getSendingPolicies (params) {
    return this.caller.request('get', '/sending_policies', 'admin_headers', params)
  }

  createSendingPolicy (data) {
    return this.caller.request('post', '/sending_policies', 'admin_headers', data)
  }

  updateSendingPolicy (params) {
    return this.caller.request('put', '/sending_policies/' + params.id, 'admin_headers', params)
  }

  // Soft delete — the backend flips status to 'disabled', it never drops the row.
  deleteSendingPolicy (params) {
    return this.caller.request('delete', '/sending_policies/' + params.id, 'admin_headers', params)
  }

  // "מועמדים חסומים" (v2). Blocking sets an authoritative flag on the applicant and
  // adds a Stargate blocklist row; CV files/abstracts are then purged by a worker.
  // Conversations and submission history are kept. Only a manager can release.
  // Index returns { can_unblock, rows }; params.include_released adds released rows.
  getBlockedCandidates (params) {
    const include_released = params && params.include_released
    return this.caller.request('get', '/blocked_candidates', 'admin_headers', include_released ? { include_released: 1 } : {})
  }

  // { applicant_id, reason } -> 201 { blocked_candidate, applicant }
  blockCandidate (params) {
    return this.caller.request('post', '/blocked_candidates', 'admin_headers', { applicant_id: params.applicant_id, reason: params.reason })
  }

  // { id: blocked_candidates row id, reason } (manager only)
  releaseBlockedCandidate (params) {
    return this.caller.request('post', '/blocked_candidates/' + params.id + '/release', 'admin_headers', { reason: params.reason })
  }

  // Re-runs a failed CV / chatbot-suppression cleanup (manager only)
  retryBlockedCleanup (params) {
    return this.caller.request('post', '/blocked_candidates/' + params.id + '/retry_cleanup', 'admin_headers', {})
  }

  getBlockedDossier (params) {
    return this.caller.request('get', '/blocked_candidates/' + params.id + '/dossier', 'admin_headers')
  }

  // Generates the dossier on demand from retained evidence (manager only, async best effort)
  createBlockedDossier (params) {
    return this.caller.request('post', '/blocked_candidates/' + params.id + '/dossier', 'admin_headers', {})
  }

  getConvSvcMessages (conversationId) {
    return this.caller.request('get', `/admin/v1/conv_svc/conversations/${conversationId}/messages`, 'admin_headers')
  }

  // מה שהצ'אטבוט למד על הכישורים של מועמד מסוים — מוצג ליד כל כישור של המשרה
  // במודל המשרה. { available, skills: [{ skill_key, status, has, detail, ... }] }.
  // מועמד שלא דיבר עם הבוט מחזיר available:false — לא שגיאה.
  getConvSvcApplicantSkillVerifications (params) {
    const applicant_id = params && params.applicant_id
    return this.caller.request('get', `/admin/v1/conv_svc/applicants/${applicant_id}/skill_verifications`, 'admin_headers')
  }

  getCompanyExposedLately (params) {
    return this.caller.request('get', `/exposed_lately`, 'admin_headers', params)
  }

  reportBug (formData) {
    // Create FormData for file upload
    const data = new FormData()

    // Add text fields
    data.append('clickedOn', formData.clickedOn)
    data.append('expectedBehavior', formData.expectedBehavior)
    data.append('actualBehavior', formData.actualBehavior)
    data.append('environment', formData.environment.join(', '))
    data.append('serialNumber', formData.serialNumber || '')
    data.append('reportedBy', formData.reportedBy)
    data.append('priority', formData.priority)

    // Add screenshot if exists
    if (formData.screenshot) {
      data.append('screenshot', formData.screenshot)
    }

    // Use direct axios call to bypass the caller system and avoid URL concatenation
    return new Promise((resolve, reject) => {
      this.caller.axios.request({
        method: 'post',
        url: 'https://jira.backend.hasama.online/api/bugs',
        headers: this.caller.getHeaders('admin_headers'),
        data: data
      })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  // "System Is Slow" report. Goes through rebase_server (admin auth), which
  // restarts nginx for instant relief AND files a Jira AI task (Opus) for the
  // root cause. Resolves on 200 (task created); rejects on 409 (a prior report
  // is still open) — the caller inspects err.response for that case.
  reportSystemSlow ({ details, reportedBy, pageUrl }) {
    return this.caller.request('post', 'report_system_slow', 'admin_headers', {
      details: details,
      reported_by: reportedBy,
      page_url: pageUrl
    })
  }

  getCompanyOffers (params) {
    return this.caller.request('get', '/company_offers/', 'admin_headers', params)
  }

  getEmployersLastMonth (params) {
    return this.caller.request('get', '/employers_last_month/', 'admin_headers', params)
  }

  post (service, endpoint, params) {
    // Generic post method for external service calls through rebase_server
    return this.caller.request('post', `/${endpoint}`, 'admin_headers', params)
  }

  // Position Post Images methods
  getPositionPostImages (params) {
    return this.caller.request('get', `/positions/${params.position_id}/post_images`, 'admin_headers', params)
  }

  postPositionPostImage (params) {
    return this.caller.request('post', `/positions/${params.position_id}/post_images`, 'admin_headers', params.form_data)
  }

  deletePositionPostImage (params) {
    return this.caller.request('delete', `/positions/${params.position_id}/post_images/${params.filename}`, 'admin_headers', params)
  }

  getPublicImageCategories () {
    return this.caller.request('get', '/public_images/categories', 'admin_headers')
  }

  getRandomPublicImage (params) {
    return this.caller.request('get', `/public_images/${params.category}/random`, 'admin_headers', { responseType: 'blob' })
      .then(blob => {
        // Convert blob to data URL so it can be used directly in <img> tags
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
      })
  }

  // Position Post PDFs methods
  getPositionPostPdfs (params) {
    return this.caller.request('get', `/positions/${params.position_id}/post_pdfs`, 'admin_headers', params)
  }

  postPositionPostPdf (params) {
    return this.caller.request('post', `/positions/${params.position_id}/post_pdfs`, 'admin_headers', params.form_data)
  }

  deletePositionPostPdf (params) {
    return this.caller.request('delete', `/positions/${params.position_id}/post_pdfs/${params.filename}`, 'admin_headers', params)
  }

  // Candidate Proposals methods
  getRelevantCandidates (params) {
    return this.caller.request('get', `/positions/${params.position_id}/candidate_proposals/relevant_candidates`, 'admin_headers', params)
  }

  sendWhatsAppToCandidates (params) {
    return this.caller.request('post', `/positions/${params.position_id}/candidate_proposals/send_whatsapp`, 'admin_headers', params)
  }

  schedulePositionCandidateReminder (params) {
    return this.caller.request('post', `/positions/${params.position_id}/candidate_proposals/schedule_reminder`, 'admin_headers', params)
  }

  // Position Scoring - Generic methods for any position (not just Solel Boneh)
  checkPositionRelatedPositions (params) {
    return this.caller.request('get', `/positions/${params.position_id}/count_related_positions`, 'admin_headers')
  }

  triggerPositionScoring (params) {
    return this.caller.request('post', `/positions/${params.position_id}/trigger_scoring`, 'admin_headers')
  }

  getOffersByType (params) {
    return this.caller.request('get', '/offers/get_by_type', 'admin_headers', params)
  }

  reportFeedError (params) {
    return this.caller.request('post', '/alerts/report_error', 'admin_headers', params)
  }

  submitConversationAnalysis (formData) {
    return this.caller.request('post', '/admin/v1/conversation_analysis/submit', 'admin_headers', formData)
  }

  approveConversationAnalysis (params) {
    return this.caller.request('post', '/admin/v1/conversation_analysis/approve', 'admin_headers', params)
  }

  pollRecordings (params) {
    return this.caller.request('get', '/admin/v1/recordings/poll', 'admin_headers', params)
  }

  getCallMonitorCalls (params = {}) {
    return this.caller.request('get', '/admin/v1/call_monitor/calls', 'admin_headers', params)
  }

  skipRecording (params) {
    return this.caller.request('post', '/admin/v1/recordings/skip', 'admin_headers', params)
  }

  submitRecordingForAnalysis (params) {
    return this.caller.request('post', '/admin/v1/recordings/submit_for_analysis', 'admin_headers', params)
  }

  submitEmployerRecording (params) {
    return this.caller.request('post', '/admin/v1/recordings/submit_employer_call', 'admin_headers', params)
  }

  // ---- CV send pattern + auto-generated accompanying texts (ARCHITECTURE §3) ----
  // Feature flags reach the frontend ONLY through this payload — never through
  // build-time $settings — so a flag flip needs no deploy.
  getCvTextFlags (params = {}) {
    return this.caller.request('get', '/admin/v1/cv_texts/flags', 'admin_headers', params)
  }

  getCvTextStatus (params) {
    return this.caller.request('get', '/admin/v1/cv_texts/status', 'admin_headers', params)
  }

  generateCvText (params) {
    return this.caller.request('post', '/admin/v1/cv_texts/generate', 'admin_headers', params)
  }

  regenerateCvText (params) {
    return this.caller.request('post', '/admin/v1/cv_texts/regenerate', 'admin_headers', params)
  }

  // R1.1 - manual-intake review queue (admin Accept-header block, like /offers).
  // Only resolve_action is sent: Rails reserves params[:action] for routing.
  resolveIntakeReview ({ offer_id, action, note }) {
    const body = { resolve_action: action }
    if (note) body.note = note
    return this.caller.request('post', `/offers/${offer_id}/intake_review/resolve`, 'admin_headers', body)
  }

  // R1.3 - read-only submission consent evidence for an applicant (+ offer).
  getSubmissionConsent ({ applicant_id, offer_id, position_id }) {
    const params = {}
    if (offer_id) params.offer_id = offer_id
    if (position_id) params.position_id = position_id
    return this.caller.request('get', `/applicants/${applicant_id}/submission_consent`, 'admin_headers', params)
  }

  // VA2 - stored abstract versions of an applicant (recruiter + chatbot).
  getApplicantAbstracts ({ applicant_id }) {
    return this.caller.request('get', `/applicants/${applicant_id}/abstracts`, 'admin_headers')
  }

  // VA2 - skills-based opening line suggestion for the CV email ({ text }).
  getCvGreetingsSuggestion ({ offer_id }) {
    return this.caller.request('get', `/offers/${offer_id}/cv_greetings_suggestion`, 'admin_headers')
  }

  // Read-only per-offer CV send + exposure history (CvSendHistoryModal).
  getCvSendHistory (params) {
    return this.caller.request('get', '/admin/v1/cv_send_history', 'admin_headers', params)
  }

  // Intake assist — recruiter phone follow-up list for stuck chatbot conversations.
  // scope: open | mine | done. Returns { tasks: [...], total }.
  getIntakeAssistTasks (params) {
    return this.caller.request('get', '/admin/v1/intake_assist_tasks', 'admin_headers', params)
  }

  claimIntakeAssistTask ({ id }) {
    return this.caller.request('post', `/admin/v1/intake_assist_tasks/${id}/claim`, 'admin_headers', {})
  }

  // outcome: completed | no_answer | callback | declined; callback_at required for callback.
  recordIntakeAssistOutcome ({ id, outcome, note, callback_at }) {
    return this.caller.request('post', `/admin/v1/intake_assist_tasks/${id}/outcome`, 'admin_headers', { outcome, note, callback_at })
  }

  releaseIntakeAssistTask ({ id }) {
    return this.caller.request('post', `/admin/v1/intake_assist_tasks/${id}/release`, 'admin_headers', {})
  }

  // "אין צורך לטפל": close an open (or own claimed) task without a call. 409 already_claimed carries the owner.
  dismissIntakeAssistTask ({ id, note }) {
    return this.caller.request('post', `/admin/v1/intake_assist_tasks/${id}/dismiss`, 'admin_headers', { note })
  }
}

export default NetworkUtils
